import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  useMediaQuery,
  useTheme,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AccountCircle,
  CreditCard,
  Settings,
  ShoppingCart,
  DoneAll as DoneAllIcon,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { useTenant } from '../../contexts/TenantContext';
import { useMarketplaceCartCount } from '../../hooks/useMarketplaceCartCount';
import { useMarketplaceStatus } from '../../hooks/useMarketplaceStatus';
import { useInAppNotifications } from '../../hooks/useInAppNotifications';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import MarketplaceCartDrawer from '../marketplace/MarketplaceCartDrawer';
import GlobalSearch from './GlobalSearch';

interface TopAppBarProps {
  onMenuClick: () => void;
}

const TopAppBar: React.FC<TopAppBarProps> = ({ onMenuClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, profile, signOut } = useAuth();
  const { config } = useTenant();
  const navigate = useNavigate();
  const cartCount = useMarketplaceCartCount();
  const { isEnabled: marketplaceEnabled } = useMarketplaceStatus();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useInAppNotifications();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState<null | HTMLElement>(null);
  const [cartOpen, setCartOpen] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleMenuClose();
    navigate('/profile');
  };

  const handleSettings = () => {
    handleMenuClose();
    navigate('/settings');
  };

  const handlePaymentMethods = () => {
    handleMenuClose();
    navigate('/payment-methods');
  };

  const handleSignOut = async () => {
    handleMenuClose();
    await signOut();
    navigate('/signin');
  };

  // Map a notification's target to a web route
  const notificationRoute = (n: { targetType?: string | null; targetId?: string | null }): string | null => {
    switch (n.targetType) {
      case 'thread':
        return '/chats';
      case 'event':
        return n.targetId ? `/events/${n.targetId}` : '/events';
      case 'group':
        return '/groups';
      case 'survey':
        return '/surveys';
      case 'newsletter':
        return '/newsletters';
      case 'announcement':
        return n.targetId ? `/announcements/${n.targetId}` : '/announcements';
      case 'member':
        return '/directory';
      case 'dues':
        return '/payment-methods';
      default:
        return null;
    }
  };

  return (
    <AppBar
      position="fixed"
      component="header"
      role="banner"
      sx={{
        zIndex: theme.zIndex.drawer + 1,
        bgcolor: 'background.paper',
        color: 'text.primary',
        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
      }}
    >
      <Toolbar>
        {/* Menu Icon (Desktop only for sidebar toggle) */}
        {!isMobile && (
          <IconButton
            edge="start"
            color="inherit"
            aria-label="toggle sidebar"
            onClick={onMenuClick}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Brand logo — the tenant wordmark logo if set, else the square icon */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL || ''}${config.branding.logoUrl || `/icons/${config.id}/icon-192.png`}`}
            alt={config.appName}
            onClick={() => navigate('/home')}
            sx={{ height: 40, width: 'auto', cursor: 'pointer', display: 'block' }}
          />
        </Box>

        {/* Global search (hidden on small screens) */}
        {isMobile ? (
          <Box sx={{ flexGrow: 1 }} />
        ) : (
          <>
            <GlobalSearch />
            {/* Spacer pushes the right-side actions to the far edge */}
            <Box sx={{ flexGrow: 1 }} />
          </>
        )}

        {/* Right Side Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Notifications */}
          <IconButton
            color="inherit"
            aria-label="notifications"
            onClick={(e) => setNotifAnchorEl(e.currentTarget)}
          >
            <Badge badgeContent={unreadCount} color="error" invisible={unreadCount === 0}>
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <Popover
            open={Boolean(notifAnchorEl)}
            anchorEl={notifAnchorEl}
            onClose={() => setNotifAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { width: 360, maxHeight: 420 } }}
          >
            <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight={600}>Notifications</Typography>
              {unreadCount > 0 && (
                <Button size="small" startIcon={<DoneAllIcon />} onClick={markAllAsRead}>
                  Mark all read
                </Button>
              )}
            </Box>
            <Divider />
            {notifications.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">No notifications</Typography>
              </Box>
            ) : (
              <List disablePadding sx={{ maxHeight: 340, overflow: 'auto' }}>
                {notifications.map((notif) => (
                  <ListItem
                    key={notif.id}
                    alignItems="flex-start"
                    sx={{
                      bgcolor: notif.read ? 'transparent' : 'action.hover',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.selected' },
                    }}
                    onClick={() => {
                      if (!notif.read) markAsRead(notif.id);
                      const route = notificationRoute(notif);
                      if (route) {
                        setNotifAnchorEl(null);
                        navigate(
                          route,
                          notif.targetType === 'thread'
                            ? { state: { openThreadId: notif.targetId || notif.threadId } }
                            : undefined,
                        );
                      }
                    }}
                  >
                    <ListItemAvatar>
                      {notif.actorPhotoUrl ? (
                        <Avatar src={notif.actorPhotoUrl} />
                      ) : (
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <NotificationsIcon fontSize="small" />
                        </Avatar>
                      )}
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={notif.read ? 400 : 600}>
                          {notif.title || notif.threadName || 'Notification'}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {notif.body || notif.content || ''}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            {formatDistanceToNow(notif.createdAt, { addSuffix: true })}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Popover>

          {/* Marketplace Cart */}
          {marketplaceEnabled && (
            <IconButton
              color="inherit"
              aria-label="marketplace cart"
              onClick={() => setCartOpen(true)}
            >
              <Badge
                badgeContent={cartCount}
                color="error"
                invisible={cartCount === 0}
              >
                <ShoppingCart />
              </Badge>
            </IconButton>
          )}

          {/* User Menu */}
          <IconButton
            onClick={handleMenuOpen}
            color="inherit"
            aria-label={`User menu for ${profile?.displayName || user?.email || 'user'}`}
            aria-controls={Boolean(anchorEl) ? 'user-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={Boolean(anchorEl)}
            sx={{ ml: 1 }}
          >
            {profile?.profileImageUrl ? (
              <Avatar
                src={profile.profileImageUrl}
                alt={`${profile.displayName} profile picture`}
                sx={{ width: 32, height: 32 }}
              />
            ) : (
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                {profile?.displayName?.[0] || user?.email?.[0]?.toUpperCase() || 'U'}
              </Avatar>
            )}
          </IconButton>

          <Menu
            id="user-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            MenuListProps={{
              'aria-labelledby': 'user-menu-button',
              role: 'menu',
            }}
          >
            <MenuItem onClick={handleProfile} role="menuitem">
              <AccountCircle sx={{ mr: 1 }} aria-hidden="true" />
              My Profile
            </MenuItem>
            <MenuItem onClick={handlePaymentMethods} role="menuitem">
              <CreditCard sx={{ mr: 1 }} aria-hidden="true" />
              Payment Methods
            </MenuItem>
            <MenuItem onClick={handleSettings} role="menuitem">
              <Settings sx={{ mr: 1 }} aria-hidden="true" />
              Settings
            </MenuItem>
            <MenuItem onClick={handleSignOut} role="menuitem">
              Sign Out
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
      <MarketplaceCartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </AppBar>
  );
};

export default TopAppBar;
